// frontend/src/components/PropertyWizard.js
import { useNavigate } from "react-router-dom";
import { CreditContext } from "../App"; // ✅ Nuevo
import { useContext, useState, useEffect } from "react";
import { Sidebar } from "primereact/sidebar";
import "primeicons/primeicons.css";
import "./PropertyWizard.css";
import SearchBox from "./Map/components/SearchBox.jsx";
import MapPreview from "./Map/MapPreview";
import { createSession } from "../api/propertiesApi";
import CreditsBlockedModal from "./CreditsBlockedModal";
import CharacteristicField from "./CharacteristicField";
import { notify } from "../utils/notify";
import { typePropertyOptions } from "./Navbar/utils/filterUtils";
import {
  WIZARD_CHARACTERISTICS,
  getApplicableCharacteristics,
  validateCharacteristicFields,
  appendCharacteristicFieldsToPayload,
} from "../utils/characteristicFields";

const apiUrl = process.env.REACT_APP_API_URL;

const MAIN_PROPERTY_OPTIONS = [
  { label: "Departamentos", value: "departamentos" },
  { label: "Casas", value: "casas" },
  { label: "PH", value: "ph" },
  { label: "Otros", value: "otros" },
];

const OTHER_PROPERTY_OPTIONS = typePropertyOptions.filter(
  (opt) => !["casas", "departamentos", "ph"].includes(opt.value),
);

/** Tipo efectivo para búsqueda / informe: slug canónico o texto libre de "Otros". */
function resolveTypeProperty(formData) {
  if (formData.typeProperty === "otros") {
    return (formData.typePropertyCustom || "").trim();
  }
  return formData.typeProperty || "";
}

const INITIAL_CHARACTERISTIC_STATE = WIZARD_CHARACTERISTICS.reduce(
  (acc, { formKey }) => {
    acc[formKey] = "";
    acc[`${formKey}UseRange`] = false;
    acc[`${formKey}Min`] = "";
    acc[`${formKey}Max`] = "";
    return acc;
  },
  {},
);

function getCreditsBlockedMessage(
  reason,
  { weeklyQuota, monthlyTokens, plan },
) {
  if (reason === "quota_exceeded") {
    const resetTxt = weeklyQuota?.resets_at
      ? ` Próxima tasación disponible: ${new Date(weeklyQuota.resets_at).toLocaleDateString("es-AR")}.`
      : "";
    return `Ya usaste tu tasación gratis de la semana.${resetTxt} Pasá a Pro para 10 tasaciones por mes.`;
  }
  if (reason === "monthly_exhausted") {
    const refillTxt = monthlyTokens?.refill_at
      ? ` Tokens nuevos: ${new Date(monthlyTokens.refill_at).toLocaleDateString("es-AR")}.`
      : "";
    const upsellTxt = plan === "pro" ? " O pasá a Business para 50/mes." : "";
    return `Te quedaste sin tokens este mes.${refillTxt}${upsellTxt}`;
  }
  return "Ups 👀 Debés pedirle a un administrador que te asigne créditos.";
}

const blockInvalidNumberKeys = (e) => {
  if (["-", "+", "e", "E", ".", ","].includes(e.key)) {
    e.preventDefault();
  }
};

function parseNonNegativeIntInput(raw) {
  if (raw === "") return "";
  if (!/^\d+$/.test(raw)) return null;
  return raw;
}

const STEP_GUIDES = {
  1: {
    icon: "pi pi-map-marker",
    title: "Paso 1: ubicación y tipo",
    purpose:
      "Definí dónde está la propiedad, si se vende o alquila y el tipo a comparar.",
    steps: [
      "Buscá la dirección o zona.",
      "Elegí operación y tipo (Casas, PH, Departamentos u Otros).",
      "Después delimitás la zona de búsqueda.",
    ],
    tip: "Una ubicación precisa evita comparables fuera de mercado.",
  },
  2: {
    icon: "pi pi-map",
    title: "Paso 2: delimitar zona",
    purpose:
      "Definí el área de búsqueda de comparables: por radio o dibujando zonas.",
    steps: [
      "Elegí buscar por radio o dibujar una o más zonas.",
      "Ajustá el radio o el polígono al mercado relevante.",
      "Revisá cuántas propiedades caen en la zona antes de seguir.",
    ],
    tip: "Una zona acotada mejora la calidad de los comparables.",
  },
  3: {
    icon: "pi pi-sliders-h",
    title: "Paso 3: características",
    purpose:
      "Ajustá la búsqueda con lo que más mueve el valor: m², ambientes, dormitorios, baños y antigüedad.",
    steps: [
      "Completá datos exactos si los tenés confirmados.",
      "Usá rangos si es estimado o hay margen comercial.",
      "Revisá que el máximo no quede por debajo del mínimo.",
    ],
    tip: "Menos ruido acá, mejores comparables después.",
  },
  4: {
    icon: "pi pi-check-square",
    title: "Paso 4: amenities e informe",
    purpose:
      "Sumá amenities relevantes y creá la tasación para revisar comparables.",
    steps: [
      "Marcá solo amenities reales de la zona.",
      "Indicá cantidad de cocheras si aplica.",
      "Creá la tasación y validá los comparables antes de compartir.",
    ],
    tip: "Amenities de más pueden empujar a comparables incorrectos.",
  },
};

const LAND_SURFACE_GUIDE = {
  icon: "pi pi-sliders-h",
  title: "Paso 3: superficie",
  purpose:
    "Indicá los metros totales del terreno para acotar los comparables.",
  steps: [
    "Completá los m² totales si los tenés confirmados.",
    "Usá un rango si el valor es estimado.",
    "Creá la tasación y validá los comparables antes de compartir.",
  ],
  tip: "En terrenos, la superficie total es el dato que más ordena el mercado.",
};

const TOTAL_STEPS = 4;

const EMPTY_AMENITIES = {
  Cochera: false,
  Pileta: false,
  Parrilla: false,
  Quincho: false,
  Jardín: false,
  Terraza: false,
  Balcón: false,
};

const PropertyWizard = ({ onComplete }) => {
  const { available, name, plan, weeklyQuota, monthlyTokens } =
    useContext(CreditContext);
  /**
   * Política FAIL OPEN: solo bloqueamos cuando estamos SEGUROS que no
   * puede. Si falta info, dejamos pasar — el backend valida igual y
   * devuelve mensaje específico (PLAN_QUOTA_EXCEEDED o MONTHLY_TOKENS_EXHAUSTED).
   */
  const canCreate = (() => {
    const hasAvailable =
      typeof available === "number" && !Number.isNaN(available);

    // legacy con tokens=0 confirmado
    if (
      plan === "legacy" &&
      hasAvailable &&
      available <= 0 &&
      !weeklyQuota &&
      !monthlyTokens
    ) {
      return { ok: false, reason: "no_tokens" };
    }
    // free con quota semanal agotada
    if (weeklyQuota && !weeklyQuota.unlimited && weeklyQuota.remaining <= 0) {
      return { ok: false, reason: "quota_exceeded" };
    }
    // pro/business con tokens mensuales agotados
    if (monthlyTokens && monthlyTokens.exhausted) {
      return { ok: false, reason: "monthly_exhausted" };
    }
    return { ok: true, reason: null };
  })();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [helpOpen, setHelpOpen] = useState(false);
  const [formData, setFormData] = useState({
    selectedPlace: null,
    operationType: "",
    typeProperty: "",
    typePropertyCustom: "",
    ...INITIAL_CHARACTERISTIC_STATE,
    age: "",
    expenses: "",
    amenities: {
      Cochera: false,
      Pileta: false,
      Parrilla: false,
      Quincho: false,
      Jardín: false,
      Terraza: false,
      Balcón: false,
    },
    parking: 0,
  });
  const [radius, setRadius] = useState(500);
  const [previewCount, setPreviewCount] = useState(null);
  // Modo de búsqueda y polígonos dibujados por el usuario en el paso de zona.
  // Persistimos ambos en la sesión para poder restaurarlos al reanudar.
  const [searchMode, setSearchMode] = useState("radius"); // 'radius' | 'polygons'
  const [polygons, setPolygons] = useState([]); // Array<Array<{lat,lng}>>
  const effectiveTypeProperty = resolveTypeProperty(formData);
  const isLand = effectiveTypeProperty === "terrenos";
  const totalSteps = isLand ? 3 : TOTAL_STEPS;
  const stepCharacteristics = getApplicableCharacteristics(effectiveTypeProperty);
  const nextStep = () => {
    // Validaciones para pasos obligatorios
    if (currentStep === 1) {
      if (!formData.selectedPlace) {
        notify.warning("Debés seleccionar la ubicación de la propiedad para continuar");
        return;
      }
      if (!formData.operationType) {
        notify.warning("Debés elegir el tipo de operación para continuar");
        return;
      }
      if (!formData.typeProperty) {
        notify.warning("Debés elegir el tipo de propiedad para continuar");
        return;
      }
      if (
        formData.typeProperty === "otros" &&
        !(formData.typePropertyCustom || "").trim()
      ) {
        notify.warning("Elegí el tipo de propiedad en Otros para continuar");
        return;
      }
    }
    if (currentStep === 2) {
      if (searchMode === "polygons" && polygons.length === 0) {
        notify.warning("Dibujá al menos una zona en el mapa para continuar");
        return;
      }
    }
    if (currentStep === 3) {
      const validation = validateCharacteristicFields(formData, stepCharacteristics);
      if (!validation.ok) {
        notify.warning(validation.message);
        return;
      }
    }
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "amenities") {
      setFormData((prev) => ({
        ...prev,
        amenities: {
          ...prev.amenities,
          [value]: checked,
        },
      }));
    } else if (type === "checkbox") {
      setFormData((prev) => {
        const newArray = checked
          ? [...prev[name], value]
          : prev[name].filter((item) => item !== value);
        return { ...prev, [name]: newArray };
      });
    } else if (type === "radio") {
      setFormData((prev) => ({ ...prev, [name]: value }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleCharacteristicExactChange = (formKey, value) => {
    setFormData((prev) => ({ ...prev, [formKey]: value }));
  };

  const handleCharacteristicBoundChange = (formKey, bound, value) => {
    setFormData((prev) => ({ ...prev, [`${formKey}${bound}`]: value }));
  };

  const handleCharacteristicRangeToggle = (formKey, checked) => {
    setFormData((prev) => {
      const next = { ...prev, [`${formKey}UseRange`]: checked };
      if (checked) {
        const exact = prev[formKey];
        if (exact) {
          next[`${formKey}Min`] = exact;
          next[`${formKey}Max`] = exact;
        }
        next[formKey] = "";
      } else {
        const min = prev[`${formKey}Min`];
        const max = prev[`${formKey}Max`];
        next[formKey] = min || max || "";
        next[`${formKey}Min`] = "";
        next[`${formKey}Max`] = "";
      }
      return next;
    });
  };

  const handleCocheraToggle = (e) => {
    const checked = e.target.checked;
    setFormData((prev) => ({
      ...prev,
      amenities: {
        ...prev.amenities,
        Cochera: checked,
      },
      parking: checked ? (prev.parking > 0 ? prev.parking : 1) : 0,
    }));
  };

  const adjustParking = (delta) => {
    setFormData((prev) => {
      const current = Math.max(1, Number(prev.parking) || 1);
      const next = Math.max(1, Math.min(20, current + delta));
      return {
        ...prev,
        parking: next,
        amenities: {
          ...prev.amenities,
          Cochera: true,
        },
      };
    });
  };

  const handlePlaceSelected = (place) => {
    console.log("Lugar seleccionado en PropertyWizard:", place); // Depuración
    setFormData((prev) => ({
      ...prev,
      selectedPlace: place
        ? {
            lat: place.geometry?.location?.lat(),
            lng: place.geometry?.location?.lng(),
            formatted_address: place.formatted_address,
          }
        : null,
    }));
  };
  const [hasRedirected, setHasRedirected] = useState(false);
  const [blockedModal, setBlockedModal] = useState({
    visible: false,
    message: "",
  });

  const showBlockedModal = (message) => {
    setBlockedModal({ visible: true, message });
  };

  const handleBlockedModalClose = () => {
    setBlockedModal({ visible: false, message: "" });
    navigate("/");
  };

  // Redirigí solo cuando estamos seguros que el user no puede crear.
  // canCreate.ok es true por default — solo lo bloqueamos con evidencia.
  useEffect(() => {
    if (hasRedirected || canCreate.ok) return;
    setHasRedirected(true);
    showBlockedModal(
      getCreditsBlockedMessage(canCreate.reason, {
        weeklyQuota,
        monthlyTokens,
        plan,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canCreate.ok, canCreate.reason, hasRedirected]);

  const handleSubmit = async () => {
    // Si el front "cree" que no puede crear, mostramos mensaje + redirect.
    // Si tiene dudas (canCreate.ok=true por default), dejamos pasar — el
    // backend devuelve 402 con mensaje específico si efectivamente no puede.
    if (!canCreate.ok) {
      showBlockedModal(
        getCreditsBlockedMessage(canCreate.reason, {
          weeklyQuota,
          monthlyTokens,
          plan,
        }),
      );
      return;
    }

    const characteristicsValidation = validateCharacteristicFields(
      formData,
      stepCharacteristics,
    );
    if (!characteristicsValidation.ok) {
      notify.warning(characteristicsValidation.message);
      return;
    }

    const submitted = { ...formData };
    if (isLand) {
      const applicable = new Set(stepCharacteristics.map((config) => config.formKey));
      WIZARD_CHARACTERISTICS.forEach(({ formKey }) => {
        if (applicable.has(formKey)) return;
        submitted[formKey] = "";
        submitted[`${formKey}UseRange`] = false;
        submitted[`${formKey}Min`] = "";
        submitted[`${formKey}Max`] = "";
      });
      submitted.amenities = { ...EMPTY_AMENITIES };
      submitted.parking = 0;
    }

    setIsLoading(true);
    try {
      const payload = {
        ...submitted,
        typeProperty: resolveTypeProperty(submitted),
        type_property: resolveTypeProperty(submitted),
        type_property_choice: submitted.typeProperty,
        type_property_custom:
          submitted.typeProperty === "otros"
            ? (submitted.typePropertyCustom || "").trim()
            : "",
        radius,
        search_mode: searchMode,
        polygons, // Array<Array<{lat,lng}>> — vacío si searchMode='radius'
      };
      const { session_id } = await createSession(payload);

      sessionStorage.setItem("current_session", session_id);

      const propertyData = {
        type_operation: submitted.operationType,
        type_property: resolveTypeProperty(submitted),
        type_property_choice: submitted.typeProperty,
        type_property_custom:
          submitted.typeProperty === "otros"
            ? (submitted.typePropertyCustom || "").trim()
            : "",
        has_pool: submitted.amenities.Pileta,
        has_grill: submitted.amenities.Parrilla,
        has_garden: submitted.amenities.Jardín,
        has_terrace: submitted.amenities.Terraza,
        has_balcony: submitted.amenities.Balcón,
        selectedPlace: submitted.selectedPlace,
        parking: submitted.parking ? parseInt(submitted.parking) : 0,
        radius,
        search_mode: searchMode,
        polygons,
        session_id,
      };

      appendCharacteristicFieldsToPayload(
        propertyData,
        submitted,
        stepCharacteristics,
      );

      onComplete(propertyData);
    } catch (err) {
      console.error(err);
      // El backend devuelve 402 con un body estructurado cuando se
      // agotó la quota. Si llegó por acá (canCreate.ok era true por
      // falta de info), mostramos mensaje claro + redirect al panel.
      const status = err?.status || err?.response?.status;
      const detail = err?.body?.detail || err?.response?.data?.detail;
      if (status === 402) {
        const msg =
          detail?.message ||
          (detail?.code === "PLAN_QUOTA_EXCEEDED"
            ? "Llegaste al límite de tu plan."
            : "Necesitás créditos para crear una tasación.");
        showBlockedModal(msg);
        return;
      }
      notify.error(err.message || "Ocurrió un error al crear la sesión.");
    } finally {
      setIsLoading(false);
    }
  };
  const amenitiesList = [
    "Pileta",
    "Parrilla",
    "Quincho",
    "Jardín",
    "Terraza",
    "Balcón",
  ];

  // Texto del badge según plan:
  //   - legacy → "Créditos: N" (tokens_assigned).
  //   - free   → "Te queda X de 1 esta semana" o "Sin tasación esta semana".
  //   - pro/business → "X de 10/50 este mes" o "Sin tokens este mes".
  let accessText = "";
  if (plan === "legacy") {
    accessText = `Créditos: ${available ?? 0}`;
  } else if (plan === "free" && weeklyQuota) {
    const r = weeklyQuota.remaining;
    accessText =
      r > 0
        ? `Te queda${r === 1 ? "" : "n"} ${r} de ${weeklyQuota.limit} esta semana`
        : "Sin tasación esta semana";
  } else if ((plan === "pro" || plan === "business") && monthlyTokens) {
    const r = monthlyTokens.remaining;
    accessText =
      r > 0
        ? `${r} de ${monthlyTokens.limit} tokens este mes`
        : "Sin tokens este mes";
  }

  const stepGuide =
    isLand && currentStep === 3 ? LAND_SURFACE_GUIDE : STEP_GUIDES[currentStep];
  const useHelpDrawer = true;

  useEffect(() => {
    setHelpOpen(false);
  }, [currentStep]);

  useEffect(() => {
    if (currentStep > totalSteps) {
      setCurrentStep(totalSteps);
    }
  }, [currentStep, totalSteps]);

  return (
    <>
    <div className={`property-wizard-container${currentStep === 2 ? " property-wizard-container--fit" : ""}`}>
      <div className="wizard-top-bar">
        <div className="credit-badge__greeting">
          Hola, <strong>{name}</strong>
        </div>
        {accessText && (
          <div className="credit-badge__credits">{accessText}</div>
        )}
      </div>

      <div className="wizard-header">
        <div className="progress-bar">
          <div
            className="progress"
            style={{
              width: `${(currentStep / totalSteps) * 100}%`,
            }}
          ></div>
        </div>
        <div className="step-indicator">
          Paso {currentStep} de {totalSteps}
        </div>
      </div>

      {stepGuide && !useHelpDrawer && (
        <section className="wizard-guide" aria-label={`Guía del ${stepGuide.title}`}>
          <div className="wizard-guide__main">
            <span className="wizard-guide__icon" aria-hidden="true">
              <i className={stepGuide.icon} />
            </span>
            <div>
              <p className="wizard-guide__eyebrow">Tutorial de tasación</p>
              <h2>{stepGuide.title}</h2>
              <p>{stepGuide.purpose}</p>
            </div>
          </div>
          <ol className="wizard-guide__steps">
            {stepGuide.steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
          <div className="wizard-guide__tip">
            <i className="pi pi-info-circle" aria-hidden="true" />
            <span>{stepGuide.tip}</span>
          </div>
        </section>
      )}

      <div className="wizard-content">
        {currentStep === 1 && (
          <div className="wizard-step wizard-step--location">
            <header className="wizard-step__intro">
              <h2 className="wizard-step__title">¿Dónde está tu propiedad?</h2>
              <p className="wizard-step__subtitle">
                Ingresá la dirección exacta o la zona donde se encuentra.
              </p>
            </header>

            <div className="wizard-step__body">
              <div className="wizard-field">
                <label className="wizard-field__label" htmlFor="pac-input">
                  Ubicación
                </label>
                <div className="custom-searchbox-wrapper">
                  <SearchBox
                    onPlaceSelected={handlePlaceSelected}
                    value={formData.selectedPlace?.formatted_address || ""}
                  />
                </div>
              </div>

              <div className="wizard-field">
                <span className="wizard-field__label" id="operation-type-label">
                  Tipo de operación
                </span>
                <div
                  className="operation-options"
                  role="radiogroup"
                  aria-labelledby="operation-type-label"
                >
                  {[
                    { label: "Venta", value: "Venta" },
                    { label: "Alquiler", value: "Alquiler" },
                    { label: "Alquiler temporal", value: "alquiler-temporal" },
                  ].map((opt) => (
                    <label
                      key={opt.value}
                      className={`operation-option${
                        formData.operationType === opt.value
                          ? " operation-option--active"
                          : ""
                      }`}
                    >
                      <input
                        type="radio"
                        name="operationType"
                        value={opt.value}
                        checked={formData.operationType === opt.value}
                        onChange={handleChange}
                      />
                      <span>{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="wizard-field">
                <span className="wizard-field__label" id="property-type-label">
                  Tipo de propiedad
                </span>
                <div
                  className="property-type-options"
                  role="radiogroup"
                  aria-labelledby="property-type-label"
                >
                  {MAIN_PROPERTY_OPTIONS.map((opt) => (
                    <label
                      key={opt.value}
                      className={`property-type-option${
                        formData.typeProperty === opt.value
                          ? " property-type-option--active"
                          : ""
                      }`}
                    >
                      <input
                        type="radio"
                        name="typeProperty"
                        value={opt.value}
                        checked={formData.typeProperty === opt.value}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            typeProperty: e.target.value,
                            typePropertyCustom:
                              e.target.value === "otros"
                                ? prev.typePropertyCustom
                                : "",
                          }))
                        }
                      />
                      <span>{opt.label}</span>
                    </label>
                  ))}
                </div>
                {formData.typeProperty === "otros" && (
                  <div className="property-type-other">
                    <label
                      className="wizard-field__label"
                      htmlFor="typePropertyCustom"
                    >
                      Especificá el tipo
                    </label>
                    <select
                      id="typePropertyCustom"
                      name="typePropertyCustom"
                      className="wizard-input"
                      value={formData.typePropertyCustom || ""}
                      autoFocus
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          typePropertyCustom: e.target.value,
                        }))
                      }
                    >
                      <option value="">Elegí un tipo</option>
                      {OTHER_PROPERTY_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <p className="property-type-other__hint">
                      Se usa en el informe y para buscar comparables.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {currentStep === 2 && formData.selectedPlace && (
          <div className="wizard-step wizard-step--zone">
            <header className="wizard-step__intro">
              <h2 className="wizard-step__title">Delimitar zona de búsqueda</h2>
              <p className="wizard-step__subtitle">
                Buscá comparables alrededor de{" "}
                <strong>
                  {formData.selectedPlace.formatted_address || "la ubicación"}
                </strong>
                .
              </p>
            </header>
            <MapPreview
              embedded
              center={formData.selectedPlace}
              radius={radius}
              typeProperty={effectiveTypeProperty}
              operationType={formData.operationType}
              apiUrl={apiUrl}
              searchMode={searchMode}
              polygons={polygons}
              onModeChange={setSearchMode}
              onPolygonsChange={setPolygons}
              onRadiusChange={setRadius}
              onCountChange={(c) => setPreviewCount(c)}
            />
          </div>
        )}

        {currentStep === 3 && (
          <div className="wizard-step wizard-step--characteristics">
            <header className="wizard-step__intro">
              <h2 className="wizard-step__title">
                {isLand ? "Superficie del terreno" : "Características de tu propiedad"}
              </h2>
              <p className="wizard-step__subtitle">
                {isLand
                  ? "Indicá los metros totales. Usá un rango si el valor es estimado."
                  : "Completá tamaño y datos principales. Usá rangos si el valor es estimado."}
              </p>
            </header>

            <div
              className={`characteristics-grid${
                isLand ? " characteristics-grid--single" : ""
              }`}
            >
              {stepCharacteristics.map((config) => (
                <CharacteristicField
                  key={config.formKey}
                  label={config.label}
                  formKey={config.formKey}
                  placeholder={config.placeholder}
                  allowDecimal={!!config.allowDecimal}
                  useRange={!!formData[`${config.formKey}UseRange`]}
                  exact={formData[config.formKey]}
                  min={formData[`${config.formKey}Min`]}
                  max={formData[`${config.formKey}Max`]}
                  onExactChange={(value) =>
                    handleCharacteristicExactChange(config.formKey, value)
                  }
                  onMinChange={(value) =>
                    handleCharacteristicBoundChange(
                      config.formKey,
                      "Min",
                      value,
                    )
                  }
                  onMaxChange={(value) =>
                    handleCharacteristicBoundChange(
                      config.formKey,
                      "Max",
                      value,
                    )
                  }
                  onToggleRange={handleCharacteristicRangeToggle}
                />
              ))}
            </div>
          </div>
        )}

        {currentStep === 4 && (
          <div className="wizard-step wizard-step--amenities">
            <header className="wizard-step__intro">
              <h2 className="wizard-step__title">Comodidades y extras</h2>
              <p className="wizard-step__subtitle">
                Marcá solo las que tenga la propiedad.
              </p>
            </header>

            <div className="amenities-grid">
              <div
                className={`amenity-option amenity-option--cochera${
                  formData.amenities.Cochera ? " is-active" : ""
                }`}
              >
                <label className="amenity-option__row">
                  <input
                    type="checkbox"
                    name="amenities"
                    value="Cochera"
                    checked={formData.amenities.Cochera}
                    onChange={handleCocheraToggle}
                  />
                  <span className="amenity-option__check" aria-hidden="true" />
                  <span className="amenity-option__label">Cochera</span>
                </label>
                {formData.amenities.Cochera && (
                  <div
                    className="amenity-option__stepper"
                    role="group"
                    aria-label="Cantidad de cocheras"
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <button
                      type="button"
                      className="amenity-option__stepper-btn"
                      onClick={() => adjustParking(-1)}
                      disabled={(formData.parking || 1) <= 1}
                      aria-label="Quitar una cochera"
                    >
                      −
                    </button>
                    <span className="amenity-option__stepper-value">
                      {formData.parking || 1}
                    </span>
                    <button
                      type="button"
                      className="amenity-option__stepper-btn"
                      onClick={() => adjustParking(1)}
                      disabled={(formData.parking || 1) >= 20}
                      aria-label="Agregar una cochera"
                    >
                      +
                    </button>
                  </div>
                )}
              </div>
              {amenitiesList.map((amenity) => (
                <label
                  key={amenity}
                  className={`amenity-option${
                    formData.amenities[amenity] ? " is-active" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    name="amenities"
                    value={amenity}
                    checked={formData.amenities[amenity]}
                    onChange={handleChange}
                  />
                  <span className="amenity-option__check" aria-hidden="true" />
                  <span className="amenity-option__label">{amenity}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="wizard-footer">
        <div className="wizard-footer__back">
          {currentStep > 1 && (
            <button
              type="button"
              className="p-button-secondary wizard-footer__btn"
              onClick={prevStep}
            >
              <i className="pi pi-arrow-left" aria-hidden="true" />
              <span>Anterior</span>
            </button>
          )}
        </div>

        <div className="wizard-footer__forward">
          {currentStep < totalSteps ? (
            <button
              type="button"
              className="details-button wizard-footer__btn wizard-footer__btn--next"
              onClick={nextStep}
            >
              <span>Siguiente</span>
              <i className="pi pi-arrow-right" aria-hidden="true" />
            </button>
          ) : (
            <button
              type="button"
              className="details-button wizard-footer__btn wizard-footer__btn--submit"
              onClick={handleSubmit}
              disabled={isLoading || !canCreate.ok}
            >
              {isLoading ? "Cargando..." : "Buscar Propiedades Similares"}
            </button>
          )}
        </div>
      </div>

      <CreditsBlockedModal
        visible={blockedModal.visible}
        message={blockedModal.message}
        onClose={handleBlockedModalClose}
      />
    </div>

    {useHelpDrawer && (
      <button
        type="button"
        className="wizard-help-trigger"
        onClick={() => setHelpOpen(true)}
      >
        <i className="pi pi-question-circle" aria-hidden="true" />
        Cómo funciona
      </button>
    )}

    {useHelpDrawer && stepGuide && (
      <Sidebar
        visible={helpOpen}
        position="right"
        onHide={() => setHelpOpen(false)}
        className="wizard-help-drawer"
        showCloseIcon
        dismissable
        modal
        aria-label={`Cómo funciona: ${stepGuide.title}`}
      >
        <div className="wizard-help-drawer__body">
          <div className="wizard-help-drawer__header">
            <span className="wizard-help-drawer__icon" aria-hidden="true">
              <i className={stepGuide.icon} />
            </span>
            <div>
              <p className="wizard-help-drawer__eyebrow">Tutorial de tasación</p>
              <h2 className="wizard-help-drawer__title">{stepGuide.title}</h2>
            </div>
          </div>
          <p className="wizard-help-drawer__lead">{stepGuide.purpose}</p>
          <ol className="wizard-help-drawer__steps">
            {stepGuide.steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
          <div className="wizard-help-drawer__tip">
            <i className="pi pi-info-circle" aria-hidden="true" />
            <span>{stepGuide.tip}</span>
          </div>
        </div>
      </Sidebar>
    )}
    </>
  );
};

export default PropertyWizard;
